import type {
  HostedAgentArtifact,
  HostedAgentArtifactKind,
  HostedAgentArtifactProvenance,
} from "@/api/client/hosted-agents-artifacts"
import type { ChatAppResponse } from "@/api/models"
import type { ArtifactEntry } from "@/features/artifacts/stores/artifact-store"
import type { HostedAgentArtifactSeed } from "@/stores/hosted-agent-artifact-seed-store"

import { useArtifactStore } from "@/features/artifacts/stores/artifact-store"
import { extractArtifacts } from "@/features/artifacts/streaming/artifact-stream-parser"
import { getResponseContent, getResponseContext } from "@/utils/response-format-utils"

/** What the publish dialog is opened with: the content to publish plus pre-fill hints. */
export type PublishArtifactDraft = {
  content: string
  title: string
  kind: HostedAgentArtifactKind
  summary: string
  provenance: HostedAgentArtifactProvenance
  /**
   * The library artifact this reply continues, when there is one: the dialog
   * offers "New version of <title> (vN+1)" and publishes with its id as
   * `parent_id`. Null for a reply that maps to no library artifact.
   */
  existing: HostedAgentArtifactSeed | null
}

/** i18n keys for the kind badge (`agents.artifacts.kind.*`). */
export const ARTIFACT_KIND_LABEL_KEYS: Record<HostedAgentArtifactKind, string> = {
  markdown: "agents.artifacts.kind.markdown",
  text: "agents.artifacts.kind.text",
  document: "agents.artifacts.kind.document",
}

/** Every format the backend's artifact library accepts, in the order the publish dialog lists them. */
export const PUBLISH_DIALOG_KINDS: readonly HostedAgentArtifactKind[] = [
  "markdown",
  "text",
  "document",
]

/**
 * Whether the publish dialog can publish a reply as this format.
 *
 * `markdown`/`text` publish the reply's text. A `document` artifact is a
 * REFERENCE to a file already in the thread (`blob_ref`, verified server-side
 * against the owner's session files) — and neither a reply nor a
 * `propose_artifact` candidate names which file, so the dialog has nothing to
 * reference. It lists the format but cannot select it (BUG-010).
 */
export function isPublishableFromReply(kind: HostedAgentArtifactKind): boolean {
  return kind === "markdown" || kind === "text"
}

/** The format the publish dialog opens on: the proposal's own, when it can be published from here. */
export function initialPublishKind(proposed: HostedAgentArtifactKind): HostedAgentArtifactKind {
  return isPublishableFromReply(proposed) ? proposed : "markdown"
}

/**
 * True when the failure is "this deployment has no artifact store", not "the
 * call failed": the backend answers `503 {code: "artifact_store_unavailable"}`
 * (or `hosted_agents_unavailable`) wherever the `hosted-agent-artifacts`
 * Cosmos container is not provisioned. A bare 503 counts too — the same
 * condition can surface before the route attaches its code.
 */
export function isArtifactStoreUnavailable(error: unknown): boolean {
  const failure = error as { status?: number; code?: string } | null
  return (
    failure?.code === "artifact_store_unavailable" ||
    failure?.code === "hosted_agents_unavailable" ||
    failure?.status === 503
  )
}

/**
 * True when the backend itself said the store is missing. Every 503 the
 * listing route can emit carries a code (`artifact_store_unavailable` or
 * `hosted_agents_unavailable`, see routes/hosted_agents.py), so a code-less
 * 503 is an infrastructure blip in front of the app — that must stay on the
 * retryable copy, not on "this deployment has no artifact store".
 */
export function isArtifactStoreMissing(error: unknown): boolean {
  const failure = error as { code?: string } | null
  return (
    failure?.code === "artifact_store_unavailable" || failure?.code === "hosted_agents_unavailable"
  )
}

/**
 * Locale key for the rail's failed listing. Measured on #3164: every agent's
 * rail showed the generic "Couldn't load the artifacts." because
 * `GET /api/agents/<slug>/artifacts` answers
 * `503 {code: "artifact_store_unavailable"}` wherever the
 * `hosted-agent-artifacts` container is not provisioned — a deployment fact no
 * retry can change, so it must not read as a transient load failure.
 */
export function artifactListErrorKey(error: unknown): string {
  return isArtifactStoreMissing(error) ? "agents.artifacts.unavailable" : "agents.artifacts.error"
}

/**
 * Locale key for a failed publish, distinguishing "the artifact library is not
 * available here" from "the publish itself failed".
 *
 * Measured D6: `POST /api/agents/<slug>/artifacts` answered 503 because the
 * store is not provisioned in dev, and the dialog just sat there — the toast it
 * raised carried only axios's `HTTP error! status: 503` line and, being
 * rendered inline behind an open modal overlay, was not visible at all. The two
 * cases need different words: one is "this deployment cannot do this", the
 * other is "try again".
 */
export function artifactPublishErrorKey(error: unknown): string {
  const failure = error as { status?: number; code?: string } | null
  if (isArtifactStoreUnavailable(failure)) {
    return "agents.artifacts.publish_unavailable"
  }
  if (failure?.code === "artifact_conflict") {
    return "agents.artifacts.publish_conflict"
  }
  if (failure?.code === "artifact_invalid") {
    return "agents.artifacts.publish_invalid"
  }
  if (failure?.code === "artifact_forbidden" || failure?.code === "artifact_provenance_denied") {
    return "agents.artifacts.publish_forbidden"
  }
  return "agents.artifacts.publish_failed"
}

export const ARTIFACT_TITLE_MAX_LENGTH = 120
export const ARTIFACT_SUMMARY_MAX_LENGTH = 500

/** A suggested title is a label, not a sentence (#3207: the first sentence was prefilled). */
export const ARTIFACT_TITLE_MAX_WORDS = 8

/** Inline Markdown marks that never belong in a title: emphasis, code, links, images. */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim()
}

/** A Markdown table row (`| a | b |`) or a delimiter row (`---|:--:`), never a title. */
const TABLE_ROW = /^\|/
const TABLE_DELIMITER_ROW = /^:?-{3,}:?(?:\s*\|\s*:?-{3,}:?)+\s*\|?$/

/**
 * Suggested artifact title from a reply (#3207 — the dialog used to prefill
 * the answer's whole first sentence):
 *
 * 1. the `title` of an inline `<artifact>` block, when the reply carries one —
 *    the model already named the piece of work;
 * 2. else the first Markdown heading, at any level, outside code fences;
 * 3. else the first {@link ARTIFACT_TITLE_MAX_WORDS} words of the first prose
 *    line, list/quote markers and inline Markdown stripped. Table rows, code
 *    fences and blank lines are not prose: a reply that opens with a table
 *    prefilled its raw header row ("| Uppgift | Ansvarig | Deadline |");
 * 4. else `fallbacks.artifactTitle` (the continued artifact's title), else —
 *    for a reply that is only a table — the localized `fallbacks.tableTitle`.
 *
 * Always capped to the server's title limit, and trailing `:`/`.`/`,` are
 * dropped so a lead-in line ("Here is the plan:") does not end on punctuation.
 */
export function deriveArtifactTitle(
  content: string,
  fallbacks: { artifactTitle?: string; tableTitle?: string } = {}
): string {
  const tagged = /<artifact\s[^>]*\btitle="([^"]+)"/.exec(content)?.[1]
  const cap = (text: string) =>
    stripInlineMarkdown(text)
      .replace(/[\s:;,.–—-]+$/, "")
      .slice(0, ARTIFACT_TITLE_MAX_LENGTH)
      .trim()
  if (tagged?.trim()) return cap(tagged)

  const prose: string[] = []
  let inFence = false
  let sawTable = false
  for (const raw of content.split("\n")) {
    const line = raw.trim()
    if (/^(```|~~~)/.test(line)) {
      inFence = !inFence
      continue
    }
    if (inFence || line.length === 0 || line.startsWith("<")) continue
    if (TABLE_ROW.test(line) || TABLE_DELIMITER_ROW.test(line)) {
      sawTable = true
      continue
    }
    const heading = /^#{1,6}\s+(.+?)\s*#*$/.exec(line)
    if (heading) return cap(heading[1])
    prose.push(line)
  }
  const first = (prose[0] ?? "").replace(/^(?:[-*+>]\s+|\d+[.)]\s+)+/, "")
  const words = stripInlineMarkdown(first).split(" ").filter(Boolean)
  const derived = cap(words.slice(0, ARTIFACT_TITLE_MAX_WORDS).join(" "))
  if (derived) return derived
  if (fallbacks.artifactTitle?.trim()) return cap(fallbacks.artifactTitle)
  return sawTable && fallbacks.tableTitle ? cap(fallbacks.tableTitle) : ""
}

/**
 * The text a reply publishes. Measured on fleettest3: a publish from a thread
 * continued from an artifact stored the RAW reply as v2 — the agent's prose
 * ("Anbudsagenten har uppdaterat tabellen.") followed by the whole
 * `<artifact …>` XML. A reply that carries an inline artifact publishes that
 * artifact, never the prose or the markup around it:
 *
 * - no `<artifact>` block → the reply itself (a plain answer);
 * - the reply's LAST complete block, when it is a `create`/`rewrite` → its
 *   inner content;
 * - an `update` (a patch, not a document) → the canvas content that patch
 *   produced for THIS message (`storeMessageId` = the chat's
 *   `${sessionId}:${index}` key), else the artifact's latest canvas content;
 * - null when nothing publishable can be recovered (a block that never
 *   closed, or a patch that did not apply) — publishing the markup is worse
 *   than not offering the dialog.
 */
/** The chat canvas's entry for an inline artifact, if this thread produced one. */
function canvasEntry(identifier: string): ArtifactEntry | undefined {
  const { artifacts } = useArtifactStore.getState()
  return Object.hasOwn(artifacts, identifier) ? artifacts[identifier] : undefined
}

export function publishableReplyContent(reply: string, storeMessageId?: string): string | null {
  const { blocks } = extractArtifacts(reply)
  if (blocks.length === 0) return reply
  const block = blocks.filter((candidate) => candidate.complete).at(-1)
  if (!block) return null
  if (block.command !== "update") return block.rawContent.trim() ? block.rawContent : null
  const entry = canvasEntry(block.identifier)
  if (!entry) return null
  const version =
    (storeMessageId
      ? entry.versions.findLast((candidate) => candidate.messageId === storeMessageId)
      : undefined) ?? entry.versions.at(-1)
  return version?.content.trim() ? version.content : null
}

/**
 * Title of the artifact a reply carries: the block's own `title`, else (an
 * `update` names no title) the canvas entry's. Undefined for a plain reply.
 */
function replyArtifactTitle(reply: string): string | undefined {
  const block = extractArtifacts(reply)
    .blocks.filter((candidate) => candidate.complete)
    .at(-1)
  if (!block) return undefined
  if (block.title !== block.identifier) return block.title
  const entryTitle = canvasEntry(block.identifier)?.title
  return entryTitle && entryTitle !== block.identifier ? entryTitle : undefined
}

/** Identifier of the last complete inline artifact in `text`, if any. */
function lastArtifactIdentifier(text: string): string | undefined {
  return extractArtifacts(text)
    .blocks.filter((candidate) => candidate.complete)
    .at(-1)?.identifier
}

/**
 * The library artifact a reply continues (fleettest3 regression): the reply's
 * own inline artifact, else the "Continue working" seed block in an earlier
 * user turn of the thread. Both carry the family's stable identifier
 * ({@link continuedArtifactIdentifier}), which is matched against the
 * library's latest version per family. Derived from the thread itself, so it
 * survives a reload or a re-opened thread — the in-memory seed binding does not.
 */
export function resolveContinuedLibraryArtifact(
  reply: string,
  thread: {
    userMessages: readonly string[]
    library: readonly HostedAgentArtifact[] | undefined
    agentSlug: string
  }
): HostedAgentArtifactSeed | null {
  const { userMessages, library, agentSlug } = thread
  if (!library?.length) return null
  const byIdentifier = new Map(
    latestPerFamily([...library]).map((artifact) => [
      continuedArtifactIdentifier(artifact),
      artifact,
    ])
  )
  const identifiers = [reply, ...userMessages.toReversed()].map(lastArtifactIdentifier)
  for (const identifier of identifiers) {
    const artifact = identifier ? byIdentifier.get(identifier) : undefined
    if (artifact) {
      return {
        agentSlug,
        artifactId: artifact.id,
        rootId: artifact.root_id || artifact.id,
        title: artifact.title,
        version: artifact.version,
      }
    }
  }
  return null
}

/**
 * Publish-dialog draft for one reply, or null when the reply has nothing
 * publishable (see {@link publishableReplyContent}). The agent's
 * `artifact_candidate` names win; otherwise the title is the inline
 * artifact's, else derived from the published content. `existing` is the
 * library artifact the reply continues (resolved from the thread), else the
 * thread's in-memory "Continue working" seed.
 */
export function buildPublishDraft(
  response: ChatAppResponse,
  options: {
    fromCandidate: boolean
    sessionId?: string | null
    index: number
    seedTitle?: string
    tableTitle: string
    /** The thread's in-memory "Continue working" seed, when still bound. */
    seed?: HostedAgentArtifactSeed | null
    /** User turns up to and including this reply's, oldest first. */
    userMessages?: readonly string[]
    /** The agent's artifact library (latest version per family). */
    library?: readonly HostedAgentArtifact[]
    agentSlug?: string
  }
): PublishArtifactDraft | null {
  const reply = getResponseContent(response)
  if (!reply.trim()) return null
  const storeMessageId = options.sessionId ? `${options.sessionId}:${options.index}` : undefined
  const content = publishableReplyContent(reply, storeMessageId)
  if (!content?.trim()) return null
  const context = getResponseContext(response)
  const candidate = options.fromCandidate ? context.artifact_candidate : undefined
  const existing =
    resolveContinuedLibraryArtifact(reply, {
      userMessages: options.userMessages ?? [],
      library: options.library,
      agentSlug: options.agentSlug ?? options.seed?.agentSlug ?? "",
    }) ??
    options.seed ??
    null
  return {
    content,
    title:
      candidate?.title ??
      context.artifact_candidate?.title ??
      (replyArtifactTitle(reply) ||
        deriveArtifactTitle(content, {
          artifactTitle: options.seedTitle ?? existing?.title,
          tableTitle: options.tableTitle,
        })),
    kind: candidate?.kind ?? "markdown",
    summary: candidate?.summary ?? context.artifact_candidate?.summary ?? "",
    provenance: {
      session_id: context.session_id ?? options.sessionId ?? undefined,
      message_id: response.messageId ?? context.message_id,
    },
    existing,
  }
}

/**
 * What the library shows for a stored artifact. The first "publish from a
 * reply" build stored the reply's prose plus the raw `<artifact …>` XML as the
 * content, and the viewer rendered that markup as text. When the content
 * carries a complete create/rewrite block, show the block's inner content.
 */
export function libraryArtifactDisplayContent(content: string): string {
  const block = extractArtifacts(content)
    .blocks.filter((candidate) => candidate.complete && candidate.command !== "update")
    .at(-1)
  return block?.rawContent.trim() ? block.rawContent : content
}

/**
 * Title the publish dialog proposes. Publishing the NEXT version of a family
 * (a thread continued from an artifact, "new version" chosen) keeps that
 * family's title — a new version is the same piece of work, not a new one.
 * Anything else takes the title derived from the reply.
 */
export function publishArtifactTitle(
  draftTitle: string,
  seed: Pick<HostedAgentArtifactSeed, "title"> | null | undefined,
  familyChoice: "new_version" | "new_family"
): string {
  if (familyChoice === "new_version" && seed?.title.trim()) return seed.title.trim()
  return draftTitle
}

/**
 * Artifact date in the UI language, formatted exactly like the thread list's
 * dates (`agentThreadDate`) — #3207: `formatFriendlyDateTime` took its month
 * name from the BROWSER locale, so a Swedish UI in an English browser read
 * "23 Sep, 12:04" next to thread dates reading "23 sep. 12:04".
 */
export function artifactDate(timestamp: string | null | undefined, locale: string): string {
  if (!timestamp) return ""
  const date = new Date(timestamp)
  if (!Number.isFinite(date.getTime())) return ""
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

/**
 * Toast after deleting one version (#3207). Other versions survive the delete,
 * so "Artifact deleted." read as if the whole family had gone: name the
 * version instead. Only the last version's delete removes the artifact.
 */
export function artifactDeletedToast(
  deleted: Pick<HostedAgentArtifact, "version">,
  remainingVersions: number
): { key: string; options?: { version: number } } {
  if (remainingVersions > 0) {
    return { key: "agents.artifacts.version_deleted", options: { version: deleted.version } }
  }
  return { key: "agents.artifacts.deleted" }
}

/**
 * The list endpoint returns the latest version per family; this guards the UI
 * against a payload that also carries older versions.
 */
export function latestPerFamily(artifacts: HostedAgentArtifact[]): HostedAgentArtifact[] {
  const byRoot = new Map<string, HostedAgentArtifact>()
  for (const artifact of artifacts) {
    const root = artifact.root_id || artifact.id
    const existing = byRoot.get(root)
    if (!existing || artifact.version > existing.version) byRoot.set(root, artifact)
  }
  return [...byRoot.values()].sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))
}

/**
 * Stable inline-artifact identifier for an artifact FAMILY: the Explorer
 * contract allows only `a-z`, `0-9` and `-`, and every version of a family
 * must continue the same inline artifact, so it derives from `root_id`.
 */
export function continuedArtifactIdentifier(
  artifact: Pick<HostedAgentArtifact, "id" | "root_id">
): string {
  const family = (artifact.root_id || artifact.id).replace(/^artifact:/, "").toLowerCase()
  const slug = family.replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "")
  return `agent-artifact-${slug || "continued"}`
}

/** Attribute values are double-quoted in the contract; a `"` in a title would end the value. */
function inlineAttribute(value: string): string {
  return value
    .replace(/"/g, "\u201d")
    .replace(/[\r\n]+/g, " ")
    .trim()
}

/**
 * Composer text for "Continue working" (#3207).
 *
 * It used to be the intro line plus the artifact's plain Markdown. The agent's
 * guidance teaches Explorer's inline-artifact contract, so the model answered
 * the first change with `command="update"` against a base the thread did not
 * have — "v0 · An update could not be applied", an empty canvas, the answer
 * lost. The artifact now travels as a real inline `<artifact>` block carrying
 * its identifier (stable per family), title, version and COMPLETE content, and
 * the hosted-agent guidance (`<artifact_bases>`) tells the model that a block
 * in a USER message is starting content: the first change is a
 * `command="create"` with the same identifier, later turns update that.
 *
 * A `document` artifact has no text content; it falls back to the summary as
 * plain text, since there is nothing an update could ever target.
 */
export function buildContinueFromArtifactText(
  intro: string,
  artifact: HostedAgentArtifact
): string {
  if (!artifact.content) return `${intro}\n\n${artifact.summary}`.trim()
  // The parser closes a block at the first `</artifact>`; one inside the
  // content would truncate the base the model is asked to continue from.
  // The seed's inner body is the text the library shows, not a second wrap of
  // a stored `<artifact>` block.
  const content = libraryArtifactDisplayContent(artifact.content)
    .replace(/<\/artifact>/gi, "&lt;/artifact&gt;")
    .trim()
  const open = [
    `identifier="${continuedArtifactIdentifier(artifact)}"`,
    `type="markdown"`,
    `title="${inlineAttribute(artifact.title)}"`,
    `version="${String(artifact.version)}"`,
    `command="create"`,
  ].join(" ")
  return `${intro}\n\n<artifact ${open}>\n${content}\n</artifact>`.trim()
}

/** A "Continue working" user turn, as the thread shows it. */
export type ContinuedArtifactSeedView = {
  /** The user's own words: the seeded block and its generated lead-in removed. */
  text: string
  /** The artifact the turn continued from, when the turn carries one. */
  seed: { title: string; version?: number } | null
}

const SEED_BLOCK = /<artifact\s((?:[^"'>]|"[^"]*"|'[^']*')*)>[\s\S]*?(?:<\/artifact>|$)/

/**
 * Inverse of {@link buildContinueFromArtifactText} for display. The user
 * bubble of a continued turn showed the whole raw `<artifact …>` seed XML;
 * it now shows the user's own text plus a compact "Continuing from <title>
 * (vN)" chip. The lead-in line the action generated ("Continue from the
 * artifact “X” (v2):") repeats the chip, so a line right before the block
 * that names the title and ends on `:` is dropped with it.
 */
export function parseContinuedArtifactSeed(message: string): ContinuedArtifactSeedView {
  const match = SEED_BLOCK.exec(message)
  if (!match) return { text: message, seed: null }
  const attrs: Record<string, string> = {}
  for (const attr of match[1].matchAll(/(\w+)=(?:"([^"]*)"|'([^']*)')/g)) {
    attrs[attr[1]] = attr[2] || attr[3] || ""
  }
  const title = (attrs.title || attrs.identifier || "").trim()
  const version = Number(attrs.version)
  const lines = message.slice(0, match.index).trimEnd().split("\n")
  const lead = lines.at(-1)?.trim() ?? ""
  if (title && lead.endsWith(":") && lead.includes(title)) lines.pop()
  const after = message.slice(match.index + match[0].length).trim()
  return {
    text: [lines.join("\n").trim(), after].filter(Boolean).join("\n\n"),
    seed: { title, version: Number.isInteger(version) && version > 0 ? version : undefined },
  }
}

/**
 * Makes the continued artifact a real base in the chat's artifact store, so
 * even an `update` the model emits against it (despite `<artifact_bases>`)
 * applies instead of failing on an empty base. The store only parses
 * ASSISTANT replies, so the inline block in the user's seed message is not
 * seen by it; this loads the same identifier/content directly. Call it from
 * the "Continue working" action, next to the composer seed.
 */
export function primeContinuedArtifactBase(artifact: HostedAgentArtifact): string | null {
  if (!artifact.content) return null
  const identifier = continuedArtifactIdentifier(artifact)
  useArtifactStore.getState().loadArtifact({
    identifier,
    type: "markdown",
    title: artifact.title,
    content: libraryArtifactDisplayContent(artifact.content),
  })
  return identifier
}
